<template>
  <div :class="{ 'master-detail': true, 'master-detail--show-detail': !isIndex }">
    <MobileHeader :label="$t('nav.monitoring_plans')">
      <div class="menu">
        <ul class="menu__items">
          <li
            v-if="hasPlans"
            class="menu-item"
          >
            <button
              class="menu-item__link menu-item__link--icon"
              type="button"
              @click="openMobileSearch"
            >
              <div class="menu-item__label">
                <UseSvg
                  :aria-label="$t('general.search')"
                  class="icon"
                  name="search"
                />
              </div>
            </button>
          </li>
          <li
            v-if="createPlanPolicy"
            class="menu-item"
          >
            <router-link
              class="menu-item__link menu-item__link--icon"
              :to="{ name: 'equipmentNewPlan' }"
            >
              <div class="menu-item__label">
                <UseSvg
                  class="icon"
                  name="plus"
                />
              </div>
            </router-link>
          </li>
        </ul>
      </div>
    </MobileHeader>

    <div class="master-detail__master">
      <div class="master-detail__page-title typo-title">
        {{ $t('nav.monitoring_plans') }}
      </div>
      <div
        v-if="hasPlans"
        :class="{
          'mobile-overlay': true,
          'show-for-medium': !mobileSearchOpen || !isIndex,
          'flex-grow': search,
        }"
      >
        <div
          :class="{
            'form form--flat': true,
            'form--separated': !mobileSearchOpen,
          }"
        >
          <div class="form__row">
            <div class="input input--light input--flat input--search">
              <div
                :class="{
                  input__prefix: true,
                  'input__prefix--hilight': search,
                }"
              >
                <UseSvg
                  class="icon"
                  name="search"
                />
              </div>
              <input
                ref="searchInput"
                v-model="search"
                :aria-label="$t('monitoring_plans.search_plans')"
                :placeholder="$t('monitoring_plans.search_plans')"
              >
              <button
                v-if="search || mobileSearchOpen"
                class="input__clear"
                type="button"
                @click="clearSearch"
              >
                <UseSvg
                  :class="{ icon: true, 'icon--small': !mobileSearchOpen }"
                  :name="mobileSearchOpen ? 'close_circle_24' : 'close_circle_16'"
                />
              </button>
            </div>
          </div>
        </div>

        <template v-if="search && searchResults">
          <HeaderBar :modifiers="['narrow', 'darkest', 'transparent-on-medium']">
            <span class="typo-small">{{ $t('general.results') }}</span>
          </HeaderBar>
          <div
            v-if="searchResults.length > 0"
            class="scroll-container scroll-container--padded"
          >
            <PlanCard
              v-for="plan in searchResults"
              :key="plan.id"
              :plan="plan"
              @click.passive="selectSearchedPlan"
            />
          </div>
          <EmptyMessage
            v-else
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
        </template>
        <div v-else />
        <!-- HACK: enforce "form--separated" still does the trick -->
      </div>

      <template v-if="!searchResults">
        <HeaderBar
          v-if="hasPlans"
          :modifiers="['narrow', 'darkest', 'sticky', 'transparent-on-medium']"
        >
          <div
            v-if="plansCount !== null && archivedPlansCount !== null"
            class="menu"
          >
            <ul class="menu__items">
              <li
                :class="{
                  'menu-item typo-small': true,
                  'menu-item--active': scope === 'plans',
                }"
              >
                <router-link
                  activeClass="menu-item__link--active menu-item__link--disabled"
                  class="menu-item__link menu-item__link--icon"
                  :to="{ name: 'equipmentPlans', params: { scope: 'plans' } }"
                >
                  <span class="menu-item__label">
                    {{ plansCount }} {{ $t('activerecord.models.monitoring_plans/plan', { count: plansCount }) }}
                  </span>
                </router-link>
              </li>
              <li
                :class="{
                  'menu-item typo-small': true,
                  'menu-item--active': scope === 'archived_plans',
                }"
              >
                <router-link
                  activeClass="menu-item__link--active menu-item__link--disabled"
                  class="menu-item__link menu-item__link--icon"
                  :to="{ name: 'equipmentPlans', params: { scope: 'archived_plans' } }"
                >
                  <span class="menu-item__label">
                    {{ archivedPlansCount }} {{ $t('monitoring_plans.archived') }}
                  </span>
                </router-link>
              </li>
            </ul>
          </div>

          <template #right>
            <Select
              v-model="sortBy"
              :items="sortByOptions"
              :placeholder="$t('general.sort_by')"
            />
          </template>
        </HeaderBar>

        <div
          v-if="plans && plans.length > 0"
          class="scroll-container scroll-container--padded"
        >
          <PlanCard
            v-for="plan in sortedPlans"
            :key="plan.id"
            :plan="plan"
          />
        </div>
        <EmptyMessage
          v-if="plans && plans.length === 0"
          :message="$t(`empty_states.${scope}.message`)"
          :modifiers="['grow', 'center']"
        >
          <NoMonitoringPlansSVG v-if="scope === 'plans'" />
          <NoMonitoringArchivedPlansSVG v-if="scope === 'archived_plans'" />
          <template #actions>
            <router-link
              v-if="createPlanPolicy"
              class="button button--inverse"
              :to="{ name: 'equipmentNewPlan' }"
            >
              <div class="button__icon">
                <PlusIcon />
              </div>
              <div class="button__label">
                {{ $t('monitoring_plans.create_new_plan') }}
              </div>
            </router-link>
          </template>
        </EmptyMessage>
      </template>

      <Loader v-if="!plans" />

      <div
        v-if="plans?.length > 0 && createPlanPolicy"
        class="form form--dark show-for-medium"
      >
        <div class="form-row">
          <router-link
            class="button button--inverse"
            :to="{ name: 'equipmentNewPlan' }"
          >
            {{ $t('monitoring_plans.create_new_plan') }}
          </router-link>
        </div>
      </div>
    </div>

    <div class="master-detail__detail">
      <EmptyMessage
        v-if="!route.params.planId"
        :message="$t('operations.monitoring_plans_will_appear_here')"
        :modifiers="['grow', 'center']"
      >
        <NoMonitoringPlansSVG class="icon" />
      </EmptyMessage>
      <router-view @update="updatePlan" />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import { screenAtLeast } from '@/functions/screen_width';
import onEscapePressed from '@/functions/on_escape_pressed';
import orderBy, { byCreatedAt } from '@/functions/order_by';
import Searcher from '@/functions/searcher';
import useAjax from '@/functions/use_ajax';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import PlanCard from '@/components/monitoring_plans/plan_card.vue';
import Select from '@/components/form/select.vue';
import UseSvg from '@/components/generic/use_svg.vue';

// Assets
import NoMonitoringArchivedPlansSVG from '@/images/no-monitoring-archived-plans.svg';
import NoMonitoringPlansSVG from '@/images/no-monitoring-plans.svg';
import PlusIcon from '@/images/plus-16.svg';

const byName = orderBy('name', x => x.toLowerCase());
const byNextDueDate = orderBy('next_due_date', x => new Date(x).getTime());

function genericSortOption(value) {
  return {
    label: I18n.t(`general.${value}`),
    value: value,
  };
}

const props = defineProps({
  organisationId: { type: String, required: true },
  equipmentId: { type: String, required: true },
  scope: { type: String, default: 'plans' },
});

const route = useRoute();
const router = useRouter();
const ajax = useAjax();

const sortByOptions = [
  ...['newest', 'oldest'].map(genericSortOption),
  { label: I18n.t('monitoring_plans.due_next'), value: 'due_next' },
  ...['alphabetical', 'alphabetical_reverse'].map(genericSortOption),
];

const plans = ref([]);
const plansCount = ref([]);
const archivedPlansCount = ref(null);
const search = ref('');
const searchResults = ref(null);
const searchInput = ref(null);
const mobileSearchOpen = ref(false);
const sortBy = ref('newest');
const createPlanPolicy = ref(null);
let searcher;

const hasPlans = computed(() => (plansCount.value > 0 || archivedPlansCount.value > 0));

const monitoringPlansPath = computed(() => `/organisations/${props.organisationId}/equipment/${props.equipmentId}/monitoring_plans`);
const isIndex = computed(() => route.name === 'equipmentPlans');

const sortedPlans = computed(() => {
  if (!plans.value) return null;
  let sortFn;
  switch (sortBy.value) {
    case 'newest': sortFn = byCreatedAt.desc; break;
    case 'oldest': sortFn = byCreatedAt.asc; break;
    case 'alphabetical': sortFn = byName.asc; break;
    case 'alphabetical_reverse': sortFn = byName.desc; break;
    case 'due_next': sortFn = byNextDueDate.desc; break;
  }
  let sorted = [...plans.value].sort(sortFn);

  if (sortBy.value === 'due_next') {
    return sorted.sort((a, b) => (a.status > b.status) ? 1 : -1);
  } else {
    return sorted;
  }
});

onMounted(() => {
  loadPlans();
});

onEscapePressed(() => {
  clearSearch();
});

watch(() => props.scope, (value, oldValue) => {
  if (plans.value && value === oldValue) return;
  loadPlans();
});

watch(search, () => {
  if (search.value) {
    searchResults.value = searcher.search(search.value);
  } else {
    searchResults.value = null;
  }
});

watch(() => route.params.planId, () => {
  if (mobileSearchOpen.value) clearSearch();
});

async function loadPlans() {
  plans.value = null;
  let { ok, data } = await ajax(`${monitoringPlansPath.value}/${props.scope}`);

  if (ok) {
    plansCount.value = data.plans_count;
    archivedPlansCount.value = data.archived_count;
    plans.value = data.plans;
    createPlanPolicy.value = data.policy?.create;
    searcher = new Searcher(plans.value);

    if (plans.value.length > 0 && isIndex.value && screenAtLeast('medium')) {
      router.replace({ name: 'equipmentPlan', params: { planId: sortedPlans.value[0].id } });
    }
  }
}

async function openMobileSearch() {
  search.value = '';
  mobileSearchOpen.value = true;

  await nextTick();
  searchInput.value.focus();
}

function clearSearch() {
  search.value = '';
  mobileSearchOpen.value = false;
}

function selectSearchedPlan() {
  if (!mobileSearchOpen.value) clearSearch();
}

function updatePlan(newPlan) {
  plans.value = plans.value.map(plan => {
    if (plan.id === newPlan.id) return newPlan;
    return plan;
  });
}
</script>
